<template>
  <v-col class="pl-0 pl-sm-2 py-3 py-sm-0" cols="12" sm="5" md="4">
    <OrderErrorDialog />
    <div class="background-color-white full-height pa-5">
      <v-row>
        <v-col>
          <div class="font-size-lg-res text-center mb-0">Your Order</div>
        </v-col>
      </v-row>
      <OrderDetails
        :awaitingCartUpdate="awaitingCartUpdate"
        :deliveryAmount="
          order.service === 'DELIVERY' ? storeInfo.delivery_amount : 0
        "
        :firstOrderDiscount="cart.firstOrderDiscount"
        :products="products"
        :totals="totals"
      />

      <v-row v-if="storeInfo && cart">
        <v-col v-if="storeInfo.pay_with_cc" class="text-center">
          <v-btn
            color="success"
            data-button="paynow"
            rounded
            :height="styles.payButtons.height"
            :width="styles.payButtons.width"
            :disabled="
              !payNowEnabled() ||
              !readyToPay ||
              !validNameAndPhone ||
              !meetMinimumOrderAmount
            "
            @click.stop="handlePayClickEvent('NOW')"
          >
            <div class="font-size-md-res text-capitalize e2e-button-paynow">
              pay now
            </div>
          </v-btn>
        </v-col>
        <v-col v-if="storeInfo.pay_in_store" class="text-center">
          <v-btn
            :color="!storeInfo.pay_with_cc ? 'success' : ''"
            :height="styles.payButtons.height"
            :width="styles.payButtons.width"
            data-button="payinstore"
            :disabled="
              !readyToPay ||
              !validNameAndPhone ||
              !meetMinimumOrderAmount ||
              order.service === 'DELIVERY'
            "
            rounded
            @click.stop="handlePayClickEvent('INSTORE')"
          >
            <div class="font-size-md-res text-capitalize e2e-button-payinstore">
              pay in store
            </div>
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          v-if="this.order.service === 'DELIVERY'"
          class="text-center cc-text"
        >
          Your credit/debit card will temporarily be authorized for
          {{ formatCurrency(totals.authorizedAmount) }}. Your statement will
          reflect only the final order total after your delivery is complete. We
          do this to account for any substitutions or other changes to your
          order.
        </v-col>
      </v-row>
    </div>
  </v-col>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import OrderDetails from "@/components/Order/Details.vue";
import OrderErrorDialog from "@/components/Order/Dialogs/Error.vue";
import { formatCurrency } from "@/util.js";

const styles = {
  xs: {
    payButtons: {
      height: "38",
      width: "140",
    },
  },
  smAndUp: {
    payButtons: {
      height: "48",
      width: "180",
    },
  },
};

const PAYMENT = Object.freeze({
  PAY_IN_STORE: true,
  PAY_NOW: false,
});

export default {
  name: "SummarizeOrder",
  components: {
    OrderDetails,
    OrderErrorDialog,
  },
  computed: {
    ...mapGetters("cart", {
      awaitingCartUpdate: "awaitingCartUpdate",
    }),
    ...mapState({
      cart: (state) => state.cart,
      order: (state) => state.orders.order,
      sharedLibPromos: (state) => state.promotions.sharedLibPromos,
      storeInfo: (state) => state.store.info,
      readyToPay: (state) => state.orders.order.readyToPay,
    }),
    bringgDeliveryReadyToPay() {
      return (
        this.storeInfo.delivery_name === "Bringg" &&
        !!this.cart.bringgDeliveryETA
      );
    },
    meetMinimumOrderAmount() {
      return this.storeInfo
        ? this.totals.subtotal >= this.storeInfo.min_order_amount
        : null;
    },
    styles() {
      return this.$vuetify.breakpoint.xsOnly ? styles.xs : styles.smAndUp;
    },
  },
  data() {
    return {
      deliveryServiceWithoutAddress: false,
    };
  },
  methods: {
    getServiceDay(selectedMoment) {
      const iso = selectedMoment.format("YYYY-MM-DD");
      const serviceType = this.order.service || "PICKUP";
      const serviceParam = `${serviceType.toLowerCase()}_days`;
      return this.storeInfo[serviceParam].find(({ date }) => date === iso);
    },
    formatCurrency,
    payNowEnabled() {
      if (!this.readyToPay) return false;
      if (this.cart.deliveryErrorMessage) return false;

      if (this.order.service === "DELIVERY") {
        if (this.deliveryServiceWithoutAddress) return false;
        if (!this.validNameAndPhone) return false;
        if (!this.meetMinimumOrderAmount) return false;

        if (this.storeInfo.delivery_name === "Bringg") {
          if (!this.bringgDeliveryReadyToPay) return false;
        }
      }
      return true;
    },

    handlePayClickEvent(type) {
      type === "NOW" ? this.makePayNowOrder() : this.makePayInStoreOrder();
    },

    makePayInStoreOrder() {
      this.setState(PAYMENT.PAY_IN_STORE);
      this.$store.dispatch("orders/payInStoreOrder");
      this.updateUserAddress();
      /*
       *
       * success from payInStoreOrder/authorize dispatch will clear cart
       * watch on checkout routes to home when cart empty
       *
       */
    },
    makePayNowOrder() {
      this.setState(PAYMENT.PAY_NOW);
      this.$store.dispatch("orders/payNowOrder", this.totals.total);
      this.updateUserAddress();
      /*
       *
       * success from payNowOrder/authorize dispatch will clear cart
       * watch on checkout routes to home when cart empty
       *
       */
    },
    setAlcoholOrTobaccoStatus() {
      this.$store.commit("orders/UPDATE", {
        name: "alcoholOrTobaccoProduct",
        val: this.cart.products.some((_) => _.is_alcohol || _.is_tobacco),
      });
    },
    setPaymentMethod(paymentMethod) {
      this.$store.commit("orders/UPDATE", { name: "noCC", val: paymentMethod });
    },
    setState(paymentMethod) {
      this.setAlcoholOrTobaccoStatus();
      this.setPaymentMethod(paymentMethod);
    },
    updateUserAddress() {
      this.$store.dispatch("user/update", { address: this.order.address });
    },
    verifyReadyToPay() {
      const { selectedDate, selectedTime, serviceNow } = this.order;
      const readyToPay =
        !!(selectedDate.iso && selectedTime.time) || serviceNow;
      this.$store.dispatch("orders/setReadyToPay", readyToPay);
    },
  },
  props: {
    addressState: String,
    products: Array,
    totals: Object,
    validNameAndPhone: Boolean,
  },
  watch: {
    "order.address.full_address": function () {
      this.verifyReadyToPay();
      this.deliveryServiceWithoutAddress =
        this.order.service !== "DELIVERY"
          ? false
          : !this.order.address.full_address ||
            this.order.address.full_address !== this.order.lastFormattedAddress;
    },
    "order.lastFormattedAddress": function () {
      this.deliveryServiceWithoutAddress =
        this.order.service !== "DELIVERY"
          ? false
          : !this.order.address.full_address ||
            this.order.address.full_address !== this.order.lastFormattedAddress;
    },
    "order.selectedDate": function () {
      this.verifyReadyToPay();
    },
    "order.selectedTime": function () {
      this.verifyReadyToPay();
    },
    "order.service": function (service) {
      if (this.order.service != null) {
        this.verifyReadyToPay();
        this.deliveryServiceWithoutAddress =
          service !== "DELIVERY"
            ? false
            : !this.order.address.full_address ||
              this.order.address.full_address !==
                this.order.lastFormattedAddress;
      }
    },
    "order.serviceNow": function () {
      this.verifyReadyToPay();
    },
  },
};
</script>
<style>
.cc-text {
  font-size: 15px;
  color: #9e9e9e;
}
</style>
